import * as ObjectTypes from 'customer-data-objects/constants/ObjectTypes';
import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { IN_LIST_TYPE } from 'customer-data-filters-ui/converters/objectSeg/constants/constants';
import { RESTRICTED_FILTER_VALUE } from 'customer-data-filters-ui/utils/fieldLevelPermissions';
import { Map as ImmutableMap } from 'immutable';
import { filterFamilySupportsDynamic, getDynamicFilterFamilyBasis, makeDynamicFilterFamily, testDynamicFilterFamily } from 'customer-data-filters-ui/filterQueryFormat/DynamicFilterFamilies';
import { fromObjectSegFilterStrict } from 'customer-data-filters-ui/converters/objectSeg/fromObjectSegFilter';
import { getDSAssetFamilyForFilter, getDSAssetFamilyForType } from 'customer-data-filters-ui/converters/objectSeg/ObjectSegTypeToDSAssetFamily';
import { getObjectTypeIdSupportedFamily, isAnyEventTypeId } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import { isObjectSegPseudoContactProperty, isObjectSegPseudoProperty } from 'customer-data-filters-ui/converters/objectSeg/ObjectSegPseudoProperties';
import { isOperator } from 'customer-data-filters-ui/filterQueryFormat/operator/Operator';
import { isWorkflowInListType } from 'customer-data-filters-ui/converters/objectSeg/fromObjectSegFilter/fromListMembershipFilter';
import { toObjectSegFilterStrict } from 'customer-data-filters-ui/converters/objectSeg/toObjectSegFilter';
import { And, Or } from '../../filterQueryFormat/logic/FilterLogicGroup';
import Restricted from 'customer-data-filters-ui/filterQueryFormat/logic/Restricted';
import filterNot from 'transmute/filterNot';
import getIn from 'transmute/getIn';
import partial from 'transmute/partial';
import update from 'transmute/update';
import { isLogicGroup, LogicGroupNames } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { getLogicGroupConditionsSize } from '../../filterQueryFormat/logic/LogicGroup';
import { hasUnifiedEventsDateRefinements } from '../../permissions/gates';
import { isILSFilterBranch } from 'customer-data-filters-ui/types/objectSeg/IlsFilter';
import invariant from 'react-utils/invariant';
import { convertPropertyNameAndValueFilter, isPropertyChangedEventTypeId } from '../../strategy/propertyChangedFilterUtils';
import { CONSTANT } from './ObjectSegFilterTypes';
import { EVENT_BASED_FIELD_KEYS } from '../../strategy/eventBasedFilters';
import toRefinementFilter from './toObjectSegFilter/toRefinementFilter';
import fromNumberRefinementFilter from './fromObjectSegFilter/fromNumberRefinementFilter';
import fromDateRefinementFilter from './fromObjectSegFilter/fromDateRefinementFilter';
import { addCondition } from '../../filterQueryFormat/logic/LogicGroup';
import { resolveIsUngated } from '../../utils/UserInfoHelper';
const findUnifiedEventRefinementByName = (refinementName, refinements) => {
  const conditions = refinements && refinements.conditions ? refinements.conditions.toObject() : {};
  const refinement = Object.values(conditions).find(condition => isOperator(condition) && condition.field && condition.field.name === refinementName);
  return refinement && isOperator(refinement) ? refinement : undefined;
};
const getRefineBysFromUnifiedEvent = logicGroup => {
  if (hasUnifiedEventsDateRefinements()) {
    const dateRefinement = findUnifiedEventRefinementByName(EVENT_BASED_FIELD_KEYS.EVENT_DATE, logicGroup.refinements);
    const numOccurrencesRefinement = findUnifiedEventRefinementByName(EVENT_BASED_FIELD_KEYS.EVENT_COUNT, logicGroup.refinements);
    if (dateRefinement && numOccurrencesRefinement) {
      return Object.assign({}, toRefinementFilter(dateRefinement), toRefinementFilter(numOccurrencesRefinement));
    }
    if (dateRefinement) {
      return toRefinementFilter(dateRefinement);
    }
    if (numOccurrencesRefinement) {
      return toRefinementFilter(numOccurrencesRefinement);
    }
  } else {
    const numOccurrencesRefinement = findUnifiedEventRefinementByName(EVENT_BASED_FIELD_KEYS.EVENT_COUNT, logicGroup.refinements);
    if (numOccurrencesRefinement) {
      return toRefinementFilter(numOccurrencesRefinement);
    }
  }
  return {
    coalescingRefineBy: {
      type: 'SetOccurrencesRefineBy',
      setType: logicGroup.hasNotCompletedUnifiedEvent ? 'NONE' : 'ANY'
    }
  };
};
const createUnifiedEventBranch = (branchOperator, eventTypeId, logicGroup) => {
  const refineBys = getRefineBysFromUnifiedEvent(logicGroup);
  return Object.assign({
    eventTypeId,
    filterBranches: [],
    filterBranchOperator: branchOperator,
    filterBranchType: 'UNIFIED_EVENTS',
    filters: getLogicGroupConditionsSize(logicGroup) === 0 ? [{
      filterType: 'CONSTANT',
      shouldAccept: true
    }] : []
  }, refineBys);
};
const createBranch = (branchOperator, objectType, parentObjectType, logicGroup) => {
  const objectTypeId = getObjectTypeIdSupportedFamily(objectType);
  if (isAnyEventTypeId(objectTypeId)) {
    return createUnifiedEventBranch(branchOperator, objectType, logicGroup);
  }
  const parentObjectTypeId = getObjectTypeIdSupportedFamily(parentObjectType);
  const {
    associationCategory,
    associationTypeId
  } = logicGroup;
  const associationBranchAttributes = {
    objectTypeId,
    operator: 'IN_LIST'
  };
  if (objectTypeId === DSAssetFamilies.PRODUCT_PROPERTY && parentObjectTypeId === DSAssetFamilies.LINE_ITEM_PROPERTY) {
    associationBranchAttributes.filterBranchType = 'PROPERTY_ASSOCIATION';
    associationBranchAttributes.propertyWithObjectId = 'hs_product_id';
  } else if (associationTypeId) {
    associationBranchAttributes.filterBranchType = 'ASSOCIATION';
    associationBranchAttributes.associationCategory = associationCategory;
    associationBranchAttributes.associationTypeId = associationTypeId;
  }
  const branch = Object.assign({
    filterBranches: [],
    filterBranchOperator: branchOperator,
    filterBranchType: branchOperator,
    filters: []
  }, associationBranchAttributes.filterBranchType ? associationBranchAttributes : {});
  if (logicGroup.includeObjectsWithNoAssociatedObjects) {
    branch.coalescingRefineBy = {
      type: 'SetOccurrencesRefineBy',
      setType: 'ANY_INCLUDE_EMPTY'
    };
  }
  return branch;
};
const addFilter = (branch, filter) => {
  if ('filters' in filter) {
    const subFilters = filter.filters;
    if (Array.isArray(subFilters)) {
      return update('filters', filters => [...filters, ...subFilters], branch);
    }
  }
  return update('filters', filters => [...filters, filter], branch);
};
const addFilterBranch = (branch, branchToAdd) => update('filterBranches',
// @ts-expect-error ts-migrate(2488) FIXME: Type 'unknown' must have a '[Symbol.iterator]()' m... Remove this comment to see the full error message
filterBranches => [...filterBranches, branchToAdd], branch);
const addIntegrationFilterLine = (existingFilter, filterToAdd) => {
  const newFilter = Object.assign({}, existingFilter, {
    filterLines: [...existingFilter.filterLines, ...filterToAdd.filterLines]
  });
  return newFilter;
};
export const isPseudo = (baseFilterFamily, filterFamily) => {
  return baseFilterFamily === DSAssetFamilies.CONTACT_PROPERTY && isObjectSegPseudoContactProperty(filterFamily) || isObjectSegPseudoProperty(filterFamily);
};
const createIntegrationFilter = (filterFamily, condition, isUngated, serializerOptions) => {
  let filter;
  condition.conditions.forEach(subCondition => {
    const subConditionFilter = toObjectSegFilterStrict(subCondition, filterFamily, isUngated, serializerOptions);
    if (!filter) {
      filter = subConditionFilter;
    } else {
      filter = addIntegrationFilterLine(filter, subConditionFilter);
    }
  });
  return filter;
};

/**
 * Converts condition to filter or branch and adds it to branch
 *
 * @param {string} objectTypeOrId can be an ObjectTypeId or an objectType
 * @param {ObjectSegFilterBranch} branch
 * @param {LogicGroup | Operator} condition
 */
const addConditionToBranch = (objectTypeOrId, isUngated, serializerOptions, branch, condition) => {
  const assetFamily = getDSAssetFamilyForType(objectTypeOrId) || objectTypeOrId;
  if (isOperator(condition)) {
    const filter = toObjectSegFilterStrict(condition, assetFamily, isUngated, serializerOptions);
    return addFilter(branch, filter);
  }
  const assetFamilyOfCondition = getDSAssetFamilyForType(condition.filterFamily) || condition.filterFamily;
  const filterFamilyBasisOfCondition = getDynamicFilterFamilyBasis(assetFamilyOfCondition) || assetFamilyOfCondition;
  if (isLogicGroup(condition) && filterFamilyBasisOfCondition === DSAssetFamilies.INTEGRATION) {
    branch = addFilter(branch, createIntegrationFilter(assetFamilyOfCondition, condition, isUngated, serializerOptions));
    return branch;
  }
  if (isPseudo(assetFamily, filterFamilyBasisOfCondition)) {
    condition.conditions.forEach(subCondition => {
      const filter = toObjectSegFilterStrict(subCondition, assetFamilyOfCondition, isUngated, serializerOptions);
      branch = addFilter(branch, filter);
    });
    return branch;
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const newBranch = logicGroupToObjectSeg(condition, isUngated, serializerOptions, objectTypeOrId);
  return addFilterBranch(branch, newBranch);
};
const _filterBranchOperatorNameByLogicGroup = ImmutableMap([[LogicGroupNames.AND, 'AND'], [LogicGroupNames.OR, 'OR']]);
const getFilterBranchOperator = logicGroupRecord => _filterBranchOperatorNameByLogicGroup.get(logicGroupRecord.logicGroupName);
const isContactToLineItemFilterBranch = (parentObjectSegType, objectSegType) => {
  return parentObjectSegType === ObjectTypes.CONTACT && objectSegType === ObjectTypes.LINE_ITEM || parentObjectSegType === DSAssetFamilies.CONTACT_PROPERTY && objectSegType === DSAssetFamilies.LINE_ITEM_PROPERTY;
};
const isContactToProductFilterBranch = (parentObjectSegType, objectSegType) => {
  return parentObjectSegType === ObjectTypes.CONTACT && objectSegType === ObjectTypes.PRODUCT || parentObjectSegType === DSAssetFamilies.CONTACT_PROPERTY && objectSegType === DSAssetFamilies.PRODUCT_PROPERTY;
};

/**
 * Adds Deal into the Contact to Line Item filter tree
 *
 * Line Items aren't directly associated to the Contacts.
 * Contacts have associated Deals and Deals have associated Line Items
 * therefore Deal association branch needs to be injected
 */
const buildContactToLineItemLogicGroup = logicGroup => {
  const extendedLogicGroup = And.of(And.of(...logicGroup.conditions.values()).merge({
    filterFamily: DSAssetFamilies.LINE_ITEM_PROPERTY,
    includeObjectsWithNoAssociatedObjects: logicGroup.includeObjectsWithNoAssociatedObjects,
    associationCategory: 'HUBSPOT_DEFINED',
    associationTypeId: 19
  })).merge({
    filterFamily: DSAssetFamilies.DEAL_PROPERTY,
    includeObjectsWithNoAssociatedObjects: logicGroup.includeObjectsWithNoAssociatedObjects,
    associationCategory: 'HUBSPOT_DEFINED',
    associationTypeId: 4
  });
  return extendedLogicGroup;
};
const buildContactToProductLogicGroup = logicGroup => {
  /**
   * Adds Deal into the Contact to Product filter tree
   *
   * Products aren't directly associated to the Contacts.
   * Contacts have associated Deals therefore Deal association branch need to be injected
   */
  return And.of(And.of(...logicGroup.conditions.values()).merge({
    filterFamily: DSAssetFamilies.PRODUCT_PROPERTY,
    includeObjectsWithNoAssociatedObjects: logicGroup.includeObjectsWithNoAssociatedObjects,
    associationCategory: 'HUBSPOT_DEFINED',
    associationTypeId: 630
  })).merge({
    filterFamily: DSAssetFamilies.DEAL_PROPERTY,
    includeObjectsWithNoAssociatedObjects: logicGroup.includeObjectsWithNoAssociatedObjects,
    associationCategory: 'HUBSPOT_DEFINED',
    associationTypeId: 4
  });
};
export const logicGroupToObjectSeg = (logicGroup, isUngated, serializerOptions, parentFilterFamily) => {
  let conditions = logicGroup.conditions;
  let objectSegType = logicGroup.filterFamily;
  const branchOperator = getFilterBranchOperator(logicGroup);
  let branch;
  if (isContactToProductFilterBranch(parentFilterFamily, objectSegType)) {
    // if current logic group is an association branch between contact and
    // product inject deals and line item association branches between them and shift
    // serialization two steps back from product to deal
    // (see https://hubspot.slack.com/archives/CUNEM77AM/p1631197062035300)
    const contactToProductLogicGroup = buildContactToProductLogicGroup(logicGroup);
    branch = createBranch('AND', ObjectTypes.DEAL, ObjectTypes.CONTACT, contactToProductLogicGroup);
    conditions = contactToProductLogicGroup.conditions;
    objectSegType = ObjectTypes.DEAL;
  } else if (isContactToLineItemFilterBranch(parentFilterFamily, objectSegType)) {
    // if current logic group is an association branch between contact and
    // line item inject deals association branch between them and shift
    // serialization one step back from line item to deal
    // (see https://git.hubteam.com/HubSpot/crm-filters/issues/208)
    const contactToLineItemLogicGroup = buildContactToLineItemLogicGroup(logicGroup);
    branch = createBranch('AND', ObjectTypes.DEAL, ObjectTypes.CONTACT, contactToLineItemLogicGroup);
    conditions = contactToLineItemLogicGroup.conditions;
    objectSegType = ObjectTypes.DEAL;
  } else {
    branch = createBranch(branchOperator, objectSegType, parentFilterFamily, logicGroup);
  }
  return conditions.reduce(partial(addConditionToBranch, objectSegType, isUngated, serializerOptions), branch);
};
export const toObjectSegV2 = (logicGroup, isUngated, serializerOptions) => {
  invariant(isLogicGroup(logicGroup), `Expected ${logicGroup} to be a logic group`);
  invariant(typeof isUngated === 'function', `Expected ${isUngated} to be a function`);
  return logicGroupToObjectSeg(logicGroup, isUngated, serializerOptions);
};
const requiresGrouping = filter => isObjectSegPseudoContactProperty(getDSAssetFamilyForFilter(filter) || filter.filterType) || isObjectSegPseudoProperty(getDSAssetFamilyForFilter(filter) || filter.filterType);
const getOperatorsFromFilter = (filterFamily, filter, getOperatorFromFilter) => {
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filterFamily)) {
    const filters = filter.filterLines.map(subFilter => {
      const curFilter = Object.assign({}, filter, {
        filterLines: [subFilter]
      });
      return getOperatorFromFilter(curFilter);
    });
    return filters;
  }
  return [getOperatorFromFilter(filter)];
};
const getGroupedOperatorsFromObjSeg = (filters, getOperatorFromFilter, baseObjectTypeId, filterBranchOperator) => {
  const assetFamilies = {};
  filters.forEach((filter, index) => {
    let assetFamily = getDSAssetFamilyForFilter(filter);

    // the DSAssetFamily for IN_LIST aliases import and workflows and has to be overridden
    if (assetFamily === DSAssetFamilies.IN_LIST) {
      const inListType = getIn(['metadata', 'inListType'], filter);
      if (inListType === IN_LIST_TYPE.IMPORT) {
        assetFamily = DSAssetFamilies.IMPORT;
      } else if (isWorkflowInListType(inListType)) {
        assetFamily = DSAssetFamilies.WORKFLOW;
      }
    }
    if (assetFamily === DSAssetFamilies.INTEGRATION) {
      assetFamily = makeDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filter.eventTypeId);
    } else if (filterFamilySupportsDynamic(assetFamily)) {
      assetFamily = makeDynamicFilterFamily(assetFamily, baseObjectTypeId);
    }
    const key = testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, assetFamily) ? `${assetFamily}__${index}` : assetFamily;

    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    assetFamilies[key] = assetFamilies[key] || {
      assetFamily,
      operators: []
    };
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    assetFamilies[key].operators.push(...getOperatorsFromFilter(assetFamily, filter, getOperatorFromFilter));
  });
  return Object.values(assetFamilies).map(entry => filterBranchOperator === 'OR' ? Or.of(...entry.operators).set('filterFamily', entry.assetFamily) : And.of(...entry.operators).set('filterFamily', entry.assetFamily));
};
const getIncludeObjectsWithNoAssociatedObjects = filterBranch => {
  const {
    coalescingRefineBy
  } = filterBranch;
  if (coalescingRefineBy) {
    return coalescingRefineBy.setType === 'ANY_INCLUDE_EMPTY' && coalescingRefineBy.type === 'SetOccurrencesRefineBy';
  }
  return false;
};
const getHasNotCompletedUnifiedEvent = (objectTypeId, filterBranch) => {
  if (isAnyEventTypeId(objectTypeId)) {
    if (filterBranch.coalescingRefineBy && filterBranch.coalescingRefineBy.type === 'SetOccurrencesRefineBy' && filterBranch.coalescingRefineBy.setType === 'NONE') {
      return true;
    }
  }
  return undefined;
};
const getRefinementsFromUnifiedEventBranch = (objectTypeId, filterBranch, getField) => {
  if (isAnyEventTypeId(objectTypeId)) {
    if (hasUnifiedEventsDateRefinements()) {
      const dateField = getField([DSAssetFamilies.UNIFIED_EVENT, EVENT_BASED_FIELD_KEYS.EVENT_DATE], true);
      const dateRefinement = dateField && fromDateRefinementFilter(dateField, filterBranch);
      const eventCountField = getField([DSAssetFamilies.UNIFIED_EVENT, EVENT_BASED_FIELD_KEYS.EVENT_COUNT], true);
      const numOccurrencesRefinement = eventCountField && fromNumberRefinementFilter(eventCountField, filterBranch);
      if (dateRefinement && numOccurrencesRefinement) {
        return And.of(dateRefinement.set('field', dateField), numOccurrencesRefinement.set('field', eventCountField));
      }
      if (dateRefinement) {
        return And.of(dateRefinement.set('field', dateField));
      }
      if (numOccurrencesRefinement) {
        return And.of(numOccurrencesRefinement.set('field', eventCountField));
      }
    } else {
      const field = getField([DSAssetFamilies.UNIFIED_EVENT, EVENT_BASED_FIELD_KEYS.EVENT_COUNT], true);
      const numOccurrencesRefinement = fromNumberRefinementFilter(field, filterBranch);
      if (numOccurrencesRefinement) {
        return And.of(numOccurrencesRefinement.set('field', field));
      }
    }
  }
  return undefined;
};
const getIsContactToLineItemObjectSegFilterBranch = (baseFilterFamily, filterBranch) => {
  const maybeLineItemTypeId = getIn(['filterBranches', '0', 'objectTypeId'], filterBranch);
  return baseFilterFamily === DSAssetFamilies.CONTACT_PROPERTY && maybeLineItemTypeId === DSAssetFamilies.LINE_ITEM_PROPERTY;
};
const getIsContactToProductObjectSegFilterBranch = (baseFilterFamily, filterBranch) => {
  var _filterBranch$filterB, _filterBranch$filterB2;
  const maybeProductTypeIdIfHasPropertyAssociation = (_filterBranch$filterB = filterBranch.filterBranches) === null || _filterBranch$filterB === void 0 || (_filterBranch$filterB = _filterBranch$filterB[0]) === null || _filterBranch$filterB === void 0 || (_filterBranch$filterB = _filterBranch$filterB.filterBranches) === null || _filterBranch$filterB === void 0 || (_filterBranch$filterB = _filterBranch$filterB[0]) === null || _filterBranch$filterB === void 0 ? void 0 : _filterBranch$filterB.objectTypeId;
  const maybeProductTypeIdIfRemovedPropertyAssociation = (_filterBranch$filterB2 = filterBranch.filterBranches) === null || _filterBranch$filterB2 === void 0 || (_filterBranch$filterB2 = _filterBranch$filterB2[0]) === null || _filterBranch$filterB2 === void 0 ? void 0 : _filterBranch$filterB2.objectTypeId;
  const hasProductFilterBranch = maybeProductTypeIdIfHasPropertyAssociation === DSAssetFamilies.PRODUCT_PROPERTY || maybeProductTypeIdIfRemovedPropertyAssociation === DSAssetFamilies.PRODUCT_PROPERTY;
  return baseFilterFamily === DSAssetFamilies.CONTACT_PROPERTY && filterBranch.objectTypeId === DSAssetFamilies.DEAL_PROPERTY && hasProductFilterBranch;
};
const filterBranchToLogicGroup = (filterBranch, getField, baseFilterFamily, isUngated, serializerOptions, isRootLevelCall) => {
  const baseObjectTypeId = getObjectTypeIdSupportedFamily(baseFilterFamily);
  const isContactToProductObjectSegFilterBranch = getIsContactToProductObjectSegFilterBranch(baseFilterFamily, filterBranch);
  const isContactToLineItemObjectSegFilterBranch = getIsContactToLineItemObjectSegFilterBranch(baseFilterFamily, filterBranch);
  let filterBranches = filterBranch.filterBranches;
  let filters = filterBranch.filters;
  const {
    filterBranchOperator,
    objectType,
    objectTypeId,
    eventTypeId
  } = filterBranch;
  let parsedObjectTypeId = baseObjectTypeId;
  if (isContactToProductObjectSegFilterBranch) {
    var _filterBranch$filterB3, _filterBranch$filterB4, _filterBranch$filterB5;
    filterBranches = [];
    const hasPropertyAssociation = ((_filterBranch$filterB3 = filterBranch.filterBranches) === null || _filterBranch$filterB3 === void 0 || (_filterBranch$filterB3 = _filterBranch$filterB3[0]) === null || _filterBranch$filterB3 === void 0 || (_filterBranch$filterB3 = _filterBranch$filterB3.filterBranches) === null || _filterBranch$filterB3 === void 0 || (_filterBranch$filterB3 = _filterBranch$filterB3[0]) === null || _filterBranch$filterB3 === void 0 ? void 0 : _filterBranch$filterB3.filterBranchType) === 'PROPERTY_ASSOCIATION';
    filters = hasPropertyAssociation ? // if current filter branch is Contact -> Deal -> Line Item -> Product
    // association remove Deal and Line Item from the chain and present branch
    // as direct relationship Contact -> Product
    (_filterBranch$filterB4 = filterBranch.filterBranches) === null || _filterBranch$filterB4 === void 0 || (_filterBranch$filterB4 = _filterBranch$filterB4[0]) === null || _filterBranch$filterB4 === void 0 || (_filterBranch$filterB4 = _filterBranch$filterB4.filterBranches) === null || _filterBranch$filterB4 === void 0 || (_filterBranch$filterB4 = _filterBranch$filterB4[0]) === null || _filterBranch$filterB4 === void 0 ? void 0 : _filterBranch$filterB4.filters : // if current filter branch is Contact -> Deal -> Product
    // association remove Deal from the chain and present branch
    // as direct relationship Contact -> Product
    (_filterBranch$filterB5 = filterBranch.filterBranches) === null || _filterBranch$filterB5 === void 0 || (_filterBranch$filterB5 = _filterBranch$filterB5[0]) === null || _filterBranch$filterB5 === void 0 ? void 0 : _filterBranch$filterB5.filters;
    parsedObjectTypeId = DSAssetFamilies.PRODUCT_PROPERTY;
  } else if (isContactToLineItemObjectSegFilterBranch) {
    // if current filter branch is Contact -> Deal -> Line Item
    // association remove Deal from the chain and present branch
    // as direct relationship Contact -> Line Item
    filterBranches = [];
    filters = getIn(['filterBranches', '0', 'filters'], filterBranch);
    parsedObjectTypeId = DSAssetFamilies.LINE_ITEM_PROPERTY;
  } else if (objectTypeId) {
    parsedObjectTypeId = objectTypeId;
  } else if (objectType) {
    parsedObjectTypeId = getObjectTypeIdSupportedFamily(objectType);
  } else if (isPropertyChangedEventTypeId(eventTypeId)) {
    parsedObjectTypeId = eventTypeId;
    filters = convertPropertyNameAndValueFilter(filters.filter(filter => filter.filterType !== CONSTANT), baseFilterFamily);
  } else if (eventTypeId) {
    parsedObjectTypeId = eventTypeId;
    filters = filters.filter(filter => filter.filterType !== 'CONSTANT');
  }
  const getOperatorFromFilter = filter => fromObjectSegFilterStrict(filter, getField, parsedObjectTypeId, isUngated, serializerOptions);

  // @ts-expect-error transmute works with native data structures as well
  const filtersToConvert = filterNot(requiresGrouping, filters);
  const operators = filtersToConvert.map(getOperatorFromFilter);
  const filtersToConvertInGroups = filters.filter(requiresGrouping);
  const groupedOperators = getGroupedOperatorsFromObjSeg(filtersToConvertInGroups, getOperatorFromFilter, parsedObjectTypeId, filterBranchOperator);
  const groups = filterBranches.map(branch => filterBranchToLogicGroup(branch, getField, parsedObjectTypeId, isUngated, serializerOptions, false));

  // @ts-expect-error OperatorInstance vs LogicGroupInstance type mismatch
  return (filterBranchOperator === 'AND' ? [...operators, ...groupedOperators, ...groups].reduce((group, condition) => addCondition(condition, group), isRootLevelCall ? And.ofRoot() : And.of()) : [...operators, ...groupedOperators, ...groups].reduce((group, condition) => addCondition(condition, group), isRootLevelCall ? Or.ofRoot() : Or.of())).merge({
    filterFamily: parsedObjectTypeId,
    includeObjectsWithNoAssociatedObjects: getIncludeObjectsWithNoAssociatedObjects(filterBranch),
    associationTypeId: !isContactToProductObjectSegFilterBranch && !isContactToLineItemObjectSegFilterBranch ? filterBranch.associationTypeId : undefined,
    associationCategory: !isContactToProductObjectSegFilterBranch && !isContactToLineItemObjectSegFilterBranch ? filterBranch.associationCategory : undefined,
    hasNotCompletedUnifiedEvent: getHasNotCompletedUnifiedEvent(parsedObjectTypeId, filterBranch),
    refinements: getRefinementsFromUnifiedEventBranch(parsedObjectTypeId, filterBranch, getField)
  });
};
export const fromObjectSegV2 = (filterBranch, baseFilterFamily, getField, isUngated, serializerOptions) => {
  invariant(isILSFilterBranch(filterBranch), `Expected ${filterBranch} to be a filter branch`);
  invariant(typeof baseFilterFamily === 'string', `Expected ${baseFilterFamily} to be a string`);
  invariant(typeof getField === 'function', `Expected ${getField} to be a function`);
  if (filterBranch.filterBranchOperator === RESTRICTED_FILTER_VALUE) {
    return Restricted();
  }
  const isRootLevelCall = true;
  return filterBranchToLogicGroup(filterBranch, getField, baseFilterFamily, resolveIsUngated(isUngated), serializerOptions, isRootLevelCall);
};